// Utility functions for item form handling

/**
 * Validates the item form data
 * @param {Object} formData - The form data to validate
 * @param {File|null} image - The image file
 * @param {boolean} requireImage - Whether an image is required (true for new items, false for editing)
 * @returns {Object} - Object containing validation errors
 */
export const validateItemForm = (formData, image, requireImage = true) => {
  const newErrors = {};

  // Validate name
  if (!formData.name || formData.name.trim() === "") {
    newErrors.name = "Name is required";
  }

  // Validate image
  if (requireImage && !image) {
    newErrors.image = "Image is required";
  }

  // Validate link
  if (!formData.link || formData.link.trim() === "") {
    newErrors.link = "Link is required";
  } else if (!/^(https?:\/\/)/.test(formData.link)) {
    newErrors.link = "Link must start with http:// or https://";
  }

  // Validate platform for tools category
  if (
    formData.category === "tools" &&
    (!formData.platform || formData.platform.length === 0)
  ) {
    newErrors.platform = "At least one platform must be selected";
  }

  // Validate descriptions
  if (!formData.engDescription || formData.engDescription.trim() === "") {
    newErrors.engDescription = "English description is required";
  }

  return newErrors;
};

/**
 * Creates a preview data object for the ItemCard component
 * @param {Object} formData - The form data
 * @param {File|null} image - The image file
 * @param {string|null} imageUrl - The image URL for existing items
 * @returns {Object} - Preview data object
 */
export const createPreviewData = (formData, image, imageUrl) => {
  const previewData = {
    name: formData.name || "Item Name",
    description: [
      formData.engDescription || "English Description",
      formData.jpDescription || "Japanese Description",
    ],
    img: image ? URL.createObjectURL(image) : imageUrl,
    category_id: [{ name: formData.category }],
    platform: formData.platform || [],
    price: formData.price || "free",
    link: formData.link || "",
    linkAndroid: formData.linkAndroid || "",
    linkWindows: formData.linkWindows || "",
    tags: formData.tags ? formData.tags.split(",") : [],
    filterTag: formData.filterTag || [],
  };

  // Special handling for apps category
  if (formData.category === "apps") {
    previewData.links = {
      android: formData.linkAndroid || "",
      apple: formData.link || "",
      windows: formData.linkWindows || "",
    };
  }

  return previewData;
};

/**
 * Get category options for the form
 * @returns {Array} - Array of category options
 */
export const getCategoryOptions = () => [
  { value: "tools", label: "Tools", icon: "tool" },
  { value: "resos", label: "Resos", icon: "resos" },
  { value: "ideaz", label: "Ideaz", icon: "idea" },
  { value: "apps", label: "Apps", icon: "apps" },
];

/**
 * Get platform options for the form
 * @returns {Array} - Array of platform options
 */
export const getPlatformOptions = (category) => {
  if (category == "tools") {
    return [
      { value: "windows", label: "Windows", icon: "windows" },
      { value: "apple", label: "Apple", icon: "apple" },
      { value: "browser", label: "browser", icon: "browser" },
      { value: "android", label: "Android", icon: "android" },
    ];
  } else if (category == "apps") {
    return [
      { value: "apple", label: "Apple", icon: "apple" },

      { value: "android", label: "Android", icon: "android" },
    ];
  }
};

/**
 * Get price options for the form
 * @returns {Array} - Array of price options
 */
export const getPriceOptions = () => [
  { value: "free", label: "Free", icon: "free" },
  { value: "paid", label: "Paid", icon: "paid" },
  { value: "freemium", label: "Freemium", icon: "freemium" },
];
